import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  ListItemIcon,
  Toolbar,
  Typography,
  useTheme,
  Avatar,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import BoltIcon from '@mui/icons-material/Bolt';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuOpen(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'primary.main',
                width: 40, 
                height: 40,
                boxShadow: 1
              }}
            >
              <BoltIcon />
            </Avatar>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'block' } }}>
              MagicBlocks Wizard Data Generator
            </Typography>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => handleNavigation('/')}
              sx={{ 
                fontWeight: isActivePath('/') ? 'bold' : 'normal',
                opacity: isActivePath('/') ? 1 : 0.8,
                '&:hover': { opacity: 1 },
                borderRadius: '8px',
                px: 2
              }}
            >
              Home
            </Button>
            <Button
              color="inherit"
              startIcon={<HistoryIcon />}
              onClick={() => handleNavigation('/history')}
              sx={{ 
                fontWeight: isActivePath('/history') ? 'bold' : 'normal',
                opacity: isActivePath('/history') ? 1 : 0.8,
                '&:hover': { opacity: 1 },
                borderRadius: '8px',
                px: 2
              }}
            >
              History
            </Button>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open mobile menu"
              onClick={handleMobileMenuOpen}
              size="large"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuOpen}
              open={Boolean(mobileMenuOpen)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleNavigation('/')}>
                <ListItemIcon>
                  <HomeIcon fontSize="small" color={isActivePath('/') ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <Typography variant="body1">Home</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleNavigation('/history')}>
                <ListItemIcon>
                  <HistoryIcon fontSize="small" color={isActivePath('/history') ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <Typography variant="body1">History</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          pt: { xs: 8, sm: 9 }, // Adds padding below the AppBar
          backgroundColor: theme.palette.background.default,
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        {children}
      </Box>
    </Box>
  );
} 